import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { utils } from '../utils';
import { theme } from '../constants';
import { components } from '../components';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import {
  MdCheckCircle, MdCancel, MdAccessTime, MdCalendarToday,
  MdSubject, MdAttachFile, MdError, MdWarning,
} from 'react-icons/md';

const CheckCircleIcon = MdCheckCircle as any;
const CancelIcon = MdCancel as any;
const PendingIcon = MdAccessTime as any;
const CalendarIcon = MdCalendarToday as any;
const SubjectIcon = MdSubject as any;
const AttachFileIcon = MdAttachFile as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;

export const CorrectionDetail: React.FC = () => {
  const { state } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t, language } = useLanguage();
  const tc = (t as any).correctionDetail || {};

  const [correction, setCorrection] = useState<any>(state?.correction);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);
  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean; title: string; message: string;
    type: 'success' | 'error' | 'warning'; onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });

  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || '';
  const locale = language === 'id' ? 'id-ID' : 'en-US';

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void) => {
    setAlertConfig({ isOpen: true, title, message, type, onClose });
  };
  const hideAlert = () => {
    const cb = alertConfig.onClose;
    setAlertConfig(p => ({ ...p, isOpen: false }));
    if (cb) cb();
  };

  if (!correction) {
    return (
      <div id='screen' style={{ ...utils.flexCenter(), height: '100vh', flexDirection: 'column' }}>
        <components.Header title={tc.title || 'Detail Koreksi'} goBack={true} />
        <text.T16>{'Data tidak ditemukan.'}</text.T16>
        <components.Button title={t.common?.goBack || 'Kembali'} onClick={() => navigate(-1)} containerStyle={{ marginTop: 20, width: 200 }} />
      </div>
    );
  }

  const getStatusConfig = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved': return { color: '#10B981', bg: '#D1FAE5', icon: <CheckCircleIcon size={30} />, label: tc.approved || 'Disetujui' };
      case 'rejected': return { color: '#EF4444', bg: '#FEE2E2', icon: <CancelIcon size={30} />, label: tc.rejected || 'Ditolak' };
      default: return { color: '#F59E0B', bg: '#FEF3C7', icon: <PendingIcon size={30} />, label: tc.pending || 'Menunggu' };
    }
  };

  const formatTime = (ts: string | null) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatDatetime = (d: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleCancel = async () => {
    setShowConfirm(false);
    setIsLoading(true);
    try {
      await axiosInstance.delete(`/attendance-corrections/${correction.id}`);
      showAlert(t.common?.success || 'Berhasil', tc.cancelSuccess || 'Koreksi berhasil dibatalkan.', 'success', () => navigate(-1));
    } catch (err: any) {
      showAlert(t.common?.error || 'Error', err?.response?.data?.message || tc.cancelError || 'Gagal membatalkan.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const status = getStatusConfig(correction.status);

  const DetailRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
    <div style={{ display: 'flex', gap: 12 }}>
      <div style={{ color: '#9CA3AF', marginTop: 2, flexShrink: 0 }}>{icon}</div>
      <div>
        <text.T12 style={{ color: '#6B7280', marginBottom: 2 }}>{label}</text.T12>
        <text.T16 style={{ fontWeight: '500', color: theme.colors.mainDark }}>{value}</text.T16>
      </div>
    </div>
  );

  return (
    <div id='screen' style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <components.Header title={tc.title || 'Detail Koreksi Absensi'} goBack={true} />

      <main className='container' style={{ marginTop: 60, paddingBottom: 120 }}>

        {/* Status Banner */}
        <div style={{
          backgroundColor: status.bg, padding: '20px 20px',
          borderRadius: 16, marginBottom: 16,
          display: 'flex', alignItems: 'center', gap: 16,
          border: `1px solid ${status.color}30`,
        }}>
          <div style={{ color: status.color }}>{status.icon}</div>
          <div>
            <text.T14 style={{ color: status.color, opacity: 0.8, marginBottom: 2 }}>{tc.currentStatus || 'Status Pengajuan'}</text.T14>
            <text.H4 style={{ color: status.color }}>{status.label}</text.H4>
          </div>
        </div>

        {/* Attendance Date */}
        <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 12 }}>
          <text.H5 style={{ marginBottom: 16, borderBottom: '1px solid #F3F4F6', paddingBottom: 10 }}>
            {tc.attendanceInfo || 'Data Absensi'}
          </text.H5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <DetailRow
              icon={<CalendarIcon size={20} />}
              label={tc.date || 'Tanggal'}
              value={formatDate(correction.date)}
            />
            <DetailRow
              icon={<PendingIcon size={20} />}
              label={tc.checkIn || 'Check-in'}
              value={formatTime(correction.check_in_time)}
            />
            <DetailRow
              icon={<PendingIcon size={20} />}
              label={tc.checkOut || 'Check-out'}
              value={formatTime(correction.check_out_time)}
            />
            {(correction.break_out_time || correction.break_in_time) && (
              <>
                <DetailRow
                  icon={<PendingIcon size={20} />}
                  label={tc.breakOut || 'Mulai Istirahat'}
                  value={formatTime(correction.break_out_time)}
                />
                <DetailRow
                  icon={<PendingIcon size={20} />}
                  label={tc.breakIn || 'Selesai Istirahat'}
                  value={formatTime(correction.break_in_time)}
                />
              </>
            )}
            <DetailRow
              icon={<SubjectIcon size={20} />}
              label={tc.reason || 'Alasan'}
              value={correction.reason}
            />
          </div>
        </div>

        {/* Evidence */}
        {correction.evidence_url && (
          <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 12 }}>
            <text.H5 style={{ marginBottom: 12 }}>{tc.evidence || 'Bukti'}</text.H5>
            <div
              onClick={() => setImageModalUrl(`${imageBaseUrl}${correction.evidence_url}`)}
              style={{ cursor: 'pointer', position: 'relative', borderRadius: 10, overflow: 'hidden', display: 'inline-block' }}
            >
              <img
                src={`${imageBaseUrl}${correction.evidence_url}`}
                alt="evidence"
                style={{ width: '100%', maxWidth: 200, height: 140, objectFit: 'cover', borderRadius: 10, display: 'block' }}
              />
            </div>
          </div>
        )}

        {/* Reviewer Info */}
        {(correction.reviewed_by_name || correction.reject_reason) && (
          <div style={{ backgroundColor: '#fff', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 12 }}>
            <text.H5 style={{ marginBottom: 16, borderBottom: '1px solid #F3F4F6', paddingBottom: 10 }}>
              {tc.reviewInfo || 'Info Review'}
            </text.H5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {correction.reviewed_by_name && (
                <DetailRow
                  icon={<SubjectIcon size={20} />}
                  label={tc.reviewedBy || 'Direview oleh'}
                  value={correction.reviewed_by_name}
                />
              )}
              {correction.reject_reason && (
                <DetailRow
                  icon={<SubjectIcon size={20} />}
                  label={tc.rejectReason || 'Alasan Penolakan'}
                  value={correction.reject_reason}
                />
              )}
            </div>
          </div>
        )}

        {/* Timestamps */}
        <div style={{ padding: '4px 4px', marginBottom: 8 }}>
          <text.T12 style={{ color: '#9CA3AF' }}>
            {tc.submittedAt || 'Diajukan'}: {formatDatetime(correction.created_at)}
          </text.T12>
        </div>
      </main>

      {/* Cancel Button */}
      {correction.status === 'pending' && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          backgroundColor: '#fff', padding: '16px 24px 32px',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.05)', zIndex: 10,
        }}>
          <div className='container' style={{ padding: 0, maxWidth: 650, margin: '0 auto' }}>
            <components.Button
              title={isLoading ? (t.common?.loading || 'Memproses...') : (tc.cancelBtn || 'Batalkan Pengajuan')}
              style={{ backgroundColor: '#F3F4F6', color: '#4B5563', border: '1px solid #E5E7EB' }}
              onClick={() => setShowConfirm(true)}
            />
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 300, textAlign: 'center' }}>
            <text.H4 style={{ marginBottom: 10 }}>{tc.confirmTitle || 'Konfirmasi'}</text.H4>
            <text.T16 style={{ color: '#6B7280', marginBottom: 24 }}>{tc.cancelConfirm || 'Yakin ingin membatalkan pengajuan koreksi ini?'}</text.T16>
            <div style={{ display: 'flex', gap: 10 }}>
              <components.Button title={t.common?.cancel || 'Tidak'} colorScheme="light" containerStyle={{ flex: 1 }} onClick={() => setShowConfirm(false)} />
              <components.Button title={tc.cancelBtn || 'Ya, Batalkan'} containerStyle={{ flex: 1 }} onClick={handleCancel} />
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertConfig.isOpen && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 300, textAlign: 'center' }}>
            <div style={{ marginBottom: 12 }}>
              {alertConfig.type === 'success' && <CheckCircleIcon size={48} color="#10B981" />}
              {alertConfig.type === 'error' && <ErrorIcon size={48} color="#EF4444" />}
              {alertConfig.type === 'warning' && <WarningIcon size={48} color="#F59E0B" />}
            </div>
            <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
            <text.T16 style={{ color: '#6B7280', marginBottom: 20 }}>{alertConfig.message}</text.T16>
            <components.Button title={t.common?.ok || 'OK'} onClick={hideAlert} />
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {imageModalUrl && (
        <div
          onClick={() => setImageModalUrl(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 7000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <button onClick={() => setImageModalUrl(null)} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 40, height: 40, cursor: 'pointer', color: 'white', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          <img src={imageModalUrl} alt="evidence" onClick={e => e.stopPropagation()} style={{ maxWidth: '95vw', maxHeight: '90vh', borderRadius: 8, objectFit: 'contain' }} />
        </div>
      )}
    </div>
  );
};
